import { supabase } from '@/lib/supabase'
import type {
  Reminder,
  ReminderFormData,
  ReminderFilters,
  ReminderStats,
  ReminderType,
  ReminderStatus,
  CreateAutoReminderData,
} from '@/types/reminder'
import { defaultDelayConfigs } from '@/types/reminder'
import { notificationsService } from './notifications'
import { emailService } from './email'
import { createRelanceEmail } from '@/utils/emailTemplates'

// STORY-031: Service de gestion des relances automatiques
export const remindersService = {
  /**
   * Recuperer toutes les relances avec filtres
   */
  async getAll(filters?: ReminderFilters): Promise<{ reminders: Reminder[]; total: number }> {
    let query = supabase
      .from('reminders')
      .select(`
        *,
        contact:contacts!contact_id(id, nom, prenom, email, entreprise)
      `, { count: 'exact' })
      .order('scheduled_at', { ascending: true })

    // Appliquer les filtres
    if (filters?.search) {
      query = query.or(`subject.ilike.%${filters.search}%,contact_name.ilike.%${filters.search}%`)
    }

    if (filters?.type) {
      query = query.eq('type', filters.type)
    }

    if (filters?.priority) {
      query = query.eq('priority', filters.priority)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.dateFrom) {
      query = query.gte('scheduled_at', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('scheduled_at', filters.dateTo)
    }

    if (filters?.contactId) {
      query = query.eq('contact_id', filters.contactId)
    }

    // Pagination
    const page = filters?.page ?? 1
    const limit = filters?.limit ?? 20
    const from = (page - 1) * limit
    const to = from + limit - 1
    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) throw error
    return { reminders: data as Reminder[], total: count ?? 0 }
  },

  /**
   * Recuperer les relances en attente (a envoyer aujourd'hui ou en retard)
   */
  async getPending(): Promise<Reminder[]> {
    const today = new Date().toISOString().split('T')[0]

    const { data, error } = await supabase
      .from('reminders')
      .select(`
        *,
        contact:contacts!contact_id(id, nom, prenom, email, entreprise)
      `)
      .in('status', ['pending', 'scheduled'])
      .lte('scheduled_at', today)
      .order('priority', { ascending: false })
      .order('scheduled_at', { ascending: true })

    if (error) throw error
    return data as Reminder[]
  },

  /**
   * Recuperer une relance par ID
   */
  async getById(id: string): Promise<Reminder | null> {
    const { data, error } = await supabase
      .from('reminders')
      .select(`
        *,
        contact:contacts!contact_id(id, nom, prenom, email, entreprise)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as Reminder
  },

  /**
   * Obtenir les statistiques des relances
   */
  async getStats(): Promise<ReminderStats> {
    const { data, error } = await supabase
      .from('reminders')
      .select('status, priority')

    if (error) throw error

    const reminders = data || []

    return {
      total: reminders.length,
      pending: reminders.filter(r => r.status === 'pending').length,
      scheduled: reminders.filter(r => r.status === 'scheduled').length,
      sent: reminders.filter(r => r.status === 'sent').length,
      urgentes: reminders.filter(r => r.priority === 'urgent' && r.status !== 'sent' && r.status !== 'completed').length,
    }
  },

  /**
   * Creer une nouvelle relance
   */
  async create(data: ReminderFormData): Promise<Reminder> {
    const { data: reminder, error } = await supabase
      .from('reminders')
      .insert({
        ...data,
        status: 'pending',
        escalation_level: 0,
      })
      .select()
      .single()

    if (error) throw error

    // Creer une notification pour l'admin
    await notificationsService.create({
      type: 'reminder_due',
      titre: 'Nouvelle relance programmee',
      message: `Relance "${data.subject}" programmee pour le ${new Date(data.scheduled_at).toLocaleDateString('fr-FR')}`,
      reference_type: 'reminder',
      reference_id: reminder.id,
      priority: data.priority,
    })

    return reminder as Reminder
  },

  /**
   * Creer une relance automatique basee sur un evenement
   */
  async createAutoReminder(data: CreateAutoReminderData): Promise<Reminder> {
    const config = defaultDelayConfigs[data.type]
    const scheduledDate = data.scheduled_at || this.calculateScheduledDate(config.initial)

    return this.create({
      type: data.type,
      reference_type: data.reference_type,
      reference_id: data.reference_id,
      contact_id: data.contact_id,
      contact_email: data.contact_email,
      contact_name: data.contact_name,
      subject: data.subject,
      message: data.message,
      scheduled_at: scheduledDate,
      priority: data.priority || this.getPriorityFromType(data.type),
      status: 'scheduled',
      escalation_level: 0,
    })
  },

  /**
   * Calculer la date programmee
   */
  calculateScheduledDate(daysFromNow: number): string {
    const date = new Date()
    date.setDate(date.getDate() + daysFromNow)
    return date.toISOString()
  },

  /**
   * Determiner la priorite selon le type
   */
  getPriorityFromType(type: ReminderType): 'low' | 'medium' | 'high' | 'urgent' {
    switch (type) {
      case 'invoice_overdue':
        return 'urgent'
      case 'invoice_unpaid':
        return 'high'
      case 'signature_pending':
      case 'devis_pending':
        return 'medium'
      case 'question_pending':
        return 'low'
      default:
        return 'medium'
    }
  },

  /**
   * Mettre a jour une relance
   */
  async update(id: string, data: Partial<ReminderFormData>): Promise<Reminder> {
    const { data: reminder, error } = await supabase
      .from('reminders')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return reminder as Reminder
  },

  /**
   * Changer le statut d'une relance
   */
  async updateStatus(id: string, status: ReminderStatus): Promise<void> {
    const updateData: Record<string, unknown> = {
      status,
      updated_at: new Date().toISOString(),
    }

    if (status === 'sent') {
      updateData.sent_at = new Date().toISOString()
    }

    const { error } = await supabase
      .from('reminders')
      .update(updateData)
      .eq('id', id)

    if (error) throw error

    // Logger l'action
    await supabase.from('notification_logs').insert({
      type: 'reminder_status_change',
      titre: 'Statut relance modifie',
      message: `Nouveau statut : ${status}`,
      reference_type: 'reminder',
      reference_id: id,
    })
  },

  /**
   * Envoyer une relance maintenant
   */
  async sendNow(id: string): Promise<void> {
    const reminder = await this.getById(id)
    if (!reminder) throw new Error('Relance non trouvee')

    // Générer le HTML avec le template
    const htmlContent = createRelanceEmail({
      contactName: reminder.contact_name,
      subject: reminder.subject,
      message: reminder.message,
      actionUrl: reminder.cta_link || undefined,
      actionText: reminder.cta_text || undefined,
    })

    // Envoyer l'email de relance
    const emailResult = await emailService.sendEmail({
      to: reminder.contact_email,
      toName: reminder.contact_name,
      subject: reminder.subject,
      html: htmlContent,
      text: `${reminder.subject}\n\nBonjour ${reminder.contact_name},\n\n${reminder.message}\n\n${reminder.cta_text && reminder.cta_link ? `${reminder.cta_text}: ${reminder.cta_link}\n\n` : ''}Cordialement,\nL'équipe LesSablesWeb`,
      referenceType: 'reminder',
      referenceId: id,
    })

    if (!emailResult.success) {
      throw new Error(emailResult.error || 'Erreur lors de l\'envoi de l\'email')
    }

    // Marquer comme envoyee
    await this.updateStatus(id, 'sent')

    // Creer une notification
    await notificationsService.create({
      type: 'reminder_sent',
      titre: 'Relance envoyee',
      message: `La relance "${reminder.subject}" a ete envoyee a ${reminder.contact_name}`,
      reference_type: 'reminder',
      reference_id: id,
      priority: 'low',
    })
  },

  /**
   * Reporter une relance
   */
  async postpone(id: string, newDate: string): Promise<Reminder> {
    return this.update(id, {
      scheduled_at: newDate,
      status: 'scheduled',
    })
  },

  /**
   * Annuler une relance
   */
  async cancel(id: string): Promise<void> {
    await this.updateStatus(id, 'cancelled')
  },

  /**
   * Escalader une relance (creer la relance suivante)
   */
  async escalate(id: string): Promise<Reminder | null> {
    const reminder = await this.getById(id)
    if (!reminder) throw new Error('Relance non trouvee')

    const config = defaultDelayConfigs[reminder.type]
    const nextLevel = reminder.escalation_level + 1

    // Verifier si on peut encore escalader
    if (nextLevel >= config.maxReminders) {
      return null
    }

    // Calculer le delai pour le niveau suivant
    let delay: number
    switch (nextLevel) {
      case 1:
        delay = config.escalation1
        break
      case 2:
        delay = config.escalation2
        break
      case 3:
        delay = config.escalation3
        break
      default:
        delay = config.escalation3
    }

    // Determiner la nouvelle priorite (escalade)
    const newPriority = this.escalatePriority(reminder.priority)

    // Creer la relance escaladee
    const escalatedReminder = await this.create({
      type: reminder.type,
      reference_type: reminder.reference_type,
      reference_id: reminder.reference_id,
      contact_id: reminder.contact_id,
      contact_email: reminder.contact_email,
      contact_name: reminder.contact_name,
      subject: `[Relance ${nextLevel + 1}] ${reminder.subject}`,
      message: reminder.message,
      scheduled_at: this.calculateScheduledDate(delay),
      priority: newPriority,
      status: 'scheduled',
      escalation_level: nextLevel,
      parent_reminder_id: reminder.id,
    })

    // Notifier l'admin
    await notificationsService.create({
      type: 'reminder_escalated',
      titre: 'Relance escaladee',
      message: `La relance "${reminder.subject}" a ete escaladee au niveau ${nextLevel + 1}`,
      reference_type: 'reminder',
      reference_id: escalatedReminder.id,
      priority: 'high',
    })

    return escalatedReminder
  },

  /**
   * Escalader la priorite
   */
  escalatePriority(currentPriority: string): 'low' | 'medium' | 'high' | 'urgent' {
    switch (currentPriority) {
      case 'low':
        return 'medium'
      case 'medium':
        return 'high'
      case 'high':
      case 'urgent':
        return 'urgent'
      default:
        return 'high'
    }
  },

  /**
   * Supprimer une relance
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('reminders')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  /**
   * Marquer comme complete (reponse recue)
   */
  async markAsCompleted(id: string): Promise<void> {
    await this.updateStatus(id, 'completed')

    // Annuler les relances enfants (escalades)
    await supabase
      .from('reminders')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('parent_reminder_id', id)
      .in('status', ['pending', 'scheduled'])
  },

  /**
   * Traitement automatique des relances dues
   * A appeler via un cron job ou une fonction Edge
   */
  async processDueReminders(): Promise<{ sent: number; escalated: number }> {
    const pending = await this.getPending()
    let sent = 0
    let escalated = 0

    for (const reminder of pending) {
      try {
        // Envoyer la relance
        await this.sendNow(reminder.id)
        sent++

        // Programmer l'escalade si necessaire
        const escalated_reminder = await this.escalate(reminder.id)
        if (escalated_reminder) {
          escalated++
        }
      } catch (error) {
        console.error(`Erreur lors du traitement de la relance ${reminder.id}:`, error)
      }
    }

    return { sent, escalated }
  },

  /**
   * Creer des relances automatiques pour un devis envoye
   */
  async createDevisReminders(devisId: string, contactId: string, contactEmail: string, contactName: string, devisNumero: string): Promise<void> {
    await this.createAutoReminder({
      type: 'devis_pending',
      reference_type: 'devis',
      reference_id: devisId,
      contact_id: contactId,
      contact_email: contactEmail,
      contact_name: contactName,
      subject: `Votre devis ${devisNumero} attend votre retour`,
      message: `Nous vous rappelons que votre devis ${devisNumero} est en attente de votre validation.`,
    })
  },

  /**
   * Creer des relances automatiques pour une facture envoyee
   */
  async createInvoiceReminders(invoiceId: string, contactId: string, contactEmail: string, contactName: string, invoiceNumero: string): Promise<void> {
    await this.createAutoReminder({
      type: 'invoice_unpaid',
      reference_type: 'facture',
      reference_id: invoiceId,
      contact_id: contactId,
      contact_email: contactEmail,
      contact_name: contactName,
      subject: `Facture ${invoiceNumero} en attente de paiement`,
      message: `Nous vous rappelons que votre facture ${invoiceNumero} est en attente de reglement.`,
    })
  },

  /**
   * Creer des relances pour une facture en retard
   */
  async createOverdueInvoiceReminders(invoiceId: string, contactId: string, contactEmail: string, contactName: string, invoiceNumero: string): Promise<void> {
    await this.createAutoReminder({
      type: 'invoice_overdue',
      reference_type: 'facture',
      reference_id: invoiceId,
      contact_id: contactId,
      contact_email: contactEmail,
      contact_name: contactName,
      subject: `URGENT: Facture ${invoiceNumero} en retard de paiement`,
      message: `Votre facture ${invoiceNumero} est en retard de paiement. Merci de regulariser votre situation dans les plus brefs delais.`,
      priority: 'urgent',
    })
  },
}

export default remindersService
